// @spec BCP-STR-036
'use client';

import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  useCreateAlternateSite,
  useDeleteRecoveryResource,
  useRecoveryResource,
  useUpdateAlternateSite,
  useVerifyAlternateSite,
} from '@/features/bcp/api/resources';
import { AlternateSiteDetail } from '@/features/bcp/components/AlternateSiteDetail';
import { AlternateSiteForm } from '@/features/bcp/components/AlternateSiteForm';
import type { AlternateSiteFormData } from '@/features/bcp/types/resources';
import { useOrganizations } from '@/hooks/use-organizations';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/bcp/resources/sites/$siteId')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.alternateSite'),
  }),
  component: AlternateSiteDetailPage,
});

function AlternateSiteDetailPage() {
  const { t } = useTranslation(['bcp', 'common']);
  const { siteId } = useParams({ from: '/_authenticated/bcp/resources/sites/$siteId' });
  const navigate = useNavigate();
  const { organizations, isLoading: orgsLoading } = useOrganizations();
  const activeOrg = organizations.find((org) => org.isActive);

  const isNew = siteId === 'new';
  const [isEditing, setIsEditing] = React.useState(isNew);

  const {
    data: site,
    isLoading: siteLoading,
    error: siteError,
  } = useRecoveryResource(siteId, { enabled: !isNew && Boolean(activeOrg) });

  const createMutation = useCreateAlternateSite();
  const updateMutation = useUpdateAlternateSite();
  const verifyMutation = useVerifyAlternateSite();
  const deleteMutation = useDeleteRecoveryResource();

  const handleSubmit = React.useCallback(
    (data: AlternateSiteFormData) => {
      if (isNew) {
        createMutation.mutate(data, {
          onSuccess: (created) => {
            toast.success(t('bcp:toast.alternateSiteCreated'));
            void navigate({
              to: '/bcp/resources/sites/$siteId',
              params: { siteId: created.id },
            });
          },
          onError: (error) => {
            toast.error(error.message);
          },
        });
      } else {
        updateMutation.mutate(
          { id: siteId, data },
          {
            onSuccess: () => {
              toast.success(t('bcp:toast.alternateSiteUpdated'));
              setIsEditing(false);
            },
            onError: (error) => {
              toast.error(error.message);
            },
          },
        );
      }
    },
    [isNew, siteId, createMutation, updateMutation, navigate],
  );

  const handleVerify = React.useCallback(() => {
    verifyMutation.mutate(
      { id: siteId },
      {
        onSuccess: () => {
          toast.success(t('bcp:toast.siteVerifiedSuccessfully'));
        },
        onError: (error) => {
          toast.error(error.message);
        },
      },
    );
  }, [siteId, verifyMutation]);

  const handleDelete = React.useCallback(() => {
    if (!window.confirm(t('bcp:routes.confirmDeleteAlternateSite'))) return;

    deleteMutation.mutate(siteId, {
      onSuccess: () => {
        toast.success(t('bcp:toast.alternateSiteDeleted'));
        void navigate({ to: '/bcp/resources/sites' });
      },
      onError: (error) => {
        toast.error(error.message);
      },
    });
  }, [siteId, deleteMutation, navigate]);

  const handleCancel = React.useCallback(() => {
    if (isNew) {
      void navigate({ to: '/bcp/resources/sites' });
    } else {
      setIsEditing(false);
    }
  }, [isNew, navigate]);

  if (orgsLoading || (!isNew && siteLoading)) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-48 rounded bg-muted" />
            <div className="h-64 rounded bg-muted" />
          </div>
        </div>
      </div>
    );
  }

  if (!activeOrg) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <div className="rounded-lg border border-dashed p-12 text-center">
            <h2 className="text-lg font-semibold text-foreground">{t('common:routeErrors.noOrgSelected')}</h2>
            <p className="mt-2 text-muted-foreground">
              {t('common:routeErrors.noOrgSelectedDesc')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!isNew && siteError) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <div className="rounded-lg border border-destructive p-12 text-center">
            <h2 className="text-lg font-semibold text-foreground">{t('bcp:routes.siteNotFound')}</h2>
            <p className="mt-2 text-muted-foreground">{siteError.message}</p>
            <Button
              variant="outline"
              onClick={() => void navigate({ to: '/bcp/resources/sites' })}
              className="mt-4"
            >
              {t('bcp:routes.backToSites')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-4xl space-y-6">
        {/* Back navigation */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void navigate({ to: '/bcp/resources/sites' })}
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          {t('bcp:routes.backToAlternateSites')}
        </Button>

        {isEditing ? (
          <>
            <h1 className="text-2xl font-semibold text-foreground">
              {isNew ? t('bcp:routes.createAlternateSite') : t('bcp:routes.editAlternateSite')}
            </h1>
            <AlternateSiteForm
              initialData={isNew ? undefined : site}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              isSubmitting={createMutation.isPending || updateMutation.isPending}
            />
          </>
        ) : site ? (
          <AlternateSiteDetail
            site={site}
            onEdit={() => { setIsEditing(true); }}
            onVerify={handleVerify}
            onDelete={handleDelete}
            isVerifying={verifyMutation.isPending}
          />
        ) : null}
      </div>
    </div>
  );
}
